import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeedStatus } from '@/hooks/useFeedStatus';
import { useSocketFeed } from '@/hooks/useSocketFeed';
import { getLocal, updateLocal } from '@/utils/storage';
import {
  getOptionChainInstrumentNames,
  getOptionChainInstrumentsData,
  getOptionExpiries,
  getSpotToken,
  getTicksData,
  fetchBarsIntraday,
} from '@/api/datafeed.api';
import {
  buildStrikeTokenMap,
  getStrikeList,
  applyStrikeSelection,
  getSeriesColor,
  RESOLUTION_MAP,
  PERIOD_DAYS,
  calculateChartDates,
  formatOI,
} from './multi-strike-utils';
import { toDateString, timeToLocal, findATMStrike, formatAmount } from '@/utils/helper';

export function useMultiStrike(mode) {
  const storageKey = `multistrike-${mode}`;
  const isDark = useSelector((s) => s.chart?.isDark ?? false);
  const { holidays, specialSessions } = useSelector((s) => s.market);
  const { isLiveFeedActive, isMarketOpen } = useFeedStatus();

  const saved = () => getLocal(storageKey) ?? {};

  const [instruments, setInstruments] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [expiryOptions, setExpiryOptions] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState('');

  const [strikeCount, setStrikeCount] = useState(() => saved().strikeCount ?? 5);
  const [selectionPreset, setSelectionPreset] = useState(() => saved().preset ?? 'only-atm');
  const [resolution, setResolution] = useState(() => saved().resolution ?? '5m');
  const [period, setPeriod] = useState(() => saved().period ?? '2D');

  const [showCombined, setShowCombined] = useState(() => mode === 'lp' && (saved().combined ?? true));
  const [showAtp, setShowAtp] = useState(() => mode === 'lp' && (saved().atp ?? false));
  const [showIndividual, setShowIndividual] = useState(() => saved().individual ?? true);

  const [oiView, setOiView] = useState('oi');

  const [showStrikePanel, setShowStrikePanel] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [rawOptionChain, setRawOptionChain] = useState([]);
  const [spotPrice, setSpotPrice] = useState(null);
  const [atmStrike, setAtmStrike] = useState(null);
  const [strikeTokenMap, setStrikeTokenMap] = useState({});
  const [allStrikes, setAllStrikes] = useState([]);
  const [selectedStrikes, setSelectedStrikes] = useState([]);

  const [strikeTableData, setStrikeTableData] = useState({});
  const [referenceData, setReferenceData] = useState({});

  // Chart data
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [pollingVersion, setPollingVersion] = useState(0);

  const firstLoadRef = useRef(true);
  const pollingTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const tableDataRef = useRef({});
  const pendingUpdatesRef = useRef({});
  const hasPendingUpdatesRef = useRef(false);
  const criticalPointsRef = useRef({});

  /* Tokens to subscribe for live feed (All strikes in the selector table) */
  const tokensToSubscribe = useMemo(() => {
    const tokens = [];
    allStrikes.forEach((strike) => {
      const map = strikeTokenMap[strike];
      if (!map) return;
      if (map.tokenCE) tokens.push(String(map.tokenCE));
      if (map.tokenPE) tokens.push(String(map.tokenPE));
    });
    return tokens;
  }, [allStrikes, strikeTokenMap]);

  const updateDOMMultiStrikeRow = useCallback((strike, row, criticalPoints) => {
    // Update lpCE
    const lpCE = document.getElementById(`${strike}-lpCE`);
    if (lpCE) lpCE.textContent = row.lpCE ? formatAmount(row.lpCE.toFixed(2)) : '-';

    // Update oiCE
    const oiCE = document.getElementById(`${strike}-oiCE`);
    if (oiCE) {
      oiCE.textContent = formatOI(row.oiCE);
      oiCE.className = row.oiCE === criticalPoints.maxOiCE ? 'ms-highlight-max' : row.oiCE === criticalPoints.minOiCE ? 'ms-highlight-min' : '';
    }

    // Update lpPE
    const lpPE = document.getElementById(`${strike}-lpPE`);
    if (lpPE) lpPE.textContent = row.lpPE ? formatAmount(row.lpPE.toFixed(2)) : '-';

    // Update oiPE
    const oiPE = document.getElementById(`${strike}-oiPE`);
    if (oiPE) {
      oiPE.textContent = formatOI(row.oiPE);
      oiPE.className = row.oiPE === criticalPoints.maxOiPE ? 'ms-highlight-max' : row.oiPE === criticalPoints.minOiPE ? 'ms-highlight-min' : '';
    }

    // Update PCR
    const pcr = document.getElementById(`${strike}-pcr`);
    if (pcr) {
      pcr.textContent = row.oiCE > 0 ? (row.oiPE / row.oiCE).toFixed(2) : '-';
    }

    // Update IV
    const iv = document.getElementById(`${strike}-iv`);
    if (iv) {
      iv.textContent = row.ivCE ? row.ivCE.toFixed(2) : '-';
    }
  }, []);

  /* Socket handler — updates table data in real time */
  const handleSocketMessage = useCallback(
    (token, tickData) => {
      const activeData = tableDataRef.current;
      let targetStrike = null;
      let isCE = false;

      for (const strike of Object.keys(strikeTokenMap)) {
        const map = strikeTokenMap[strike];
        if (String(map?.tokenCE) === token || String(map?.tokenPE) === token) {
          targetStrike = strike;
          isCE = String(map.tokenCE) === token;
          break;
        }
      }

      if (!targetStrike) return;

      const prefix = isCE ? 'CE' : 'PE';
      const entry = { ...(activeData[targetStrike] || {}) };
      entry[`lp${prefix}`] = tickData.lp;
      entry[`oi${prefix}`] = tickData.oi;
      entry[`vol${prefix}`] = tickData.v;
      entry[`iv${prefix}`] = tickData.iv;

      if (isCE) {
        entry.oiChgCE = tickData.oi - (referenceData[targetStrike]?.CE?.oi ?? 0);
      } else {
        entry.oiChgPE = tickData.oi - (referenceData[targetStrike]?.PE?.oi ?? 0);
      }

      activeData[targetStrike] = entry;
      pendingUpdatesRef.current[targetStrike] = entry;
      hasPendingUpdatesRef.current = true;

      // Direct DOM update bypass
      updateDOMMultiStrikeRow(targetStrike, entry, criticalPointsRef.current);
    },
    [strikeTokenMap, referenceData, updateDOMMultiStrikeRow],
  );

  useSocketFeed(isLiveFeedActive && isMarketOpen, tokensToSubscribe, handleSocketMessage);

  /* Load instruments */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      const res = await getOptionChainInstrumentNames();
      if (cancelled || !res) {
        setIsLoading(false);
        return;
      }
      const values = res?.data?.values ?? res?.values ?? [];
      if (!values.length) {
        setIsLoading(false);
        return;
      }
      const names = values.map((i) => i.tradingName);
      setInstruments(names);
      const s = saved();
      const def = s.instrument && names.includes(s.instrument) ? s.instrument
        : names.includes('NIFTY') ? 'NIFTY' : names[0];
      setSelectedInstrument(def);
      updateLocal(storageKey, { instrument: def });
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  /* Load expiries */
  useEffect(() => {
    if (!selectedInstrument) return;
    let cancelled = false;
    (async () => {
      const res = await getOptionExpiries(selectedInstrument);
      if (cancelled || !res) return;
      const expiries = res?.data?.expiries ?? res?.expiries ?? [];
      setExpiryOptions(expiries);
      const s = saved();
      const def = s.expiryDate && expiries.includes(s.expiryDate) ? s.expiryDate : expiries[0] ?? '';
      setSelectedExpiry(def);
      updateLocal(storageKey, { expiryDate: def });
    })();
    return () => { cancelled = true; };
  }, [selectedInstrument]);

  /* Load spot price */
  useEffect(() => {
    if (!selectedInstrument) return;
    let cancelled = false;
    (async () => {
      const exchange = (selectedInstrument === 'SENSEX' || selectedInstrument === 'BANKEX') ? 'BSE' : 'NSE';
      const res = await getSpotToken(selectedInstrument, exchange);
      if (cancelled || !res) return;
      const items = res?.data ?? res ?? [];
      const token = items[0]?.instrumentToken;
      if (!token) return;
      const tickRes = await getTicksData(String(token));
      if (cancelled || !tickRes) return;
      const tickData = tickRes?.data ?? tickRes ?? {};
      const lp = tickData[token]?.lp;
      if (lp) setSpotPrice(lp);
    })();
    return () => { cancelled = true; };
  }, [selectedInstrument, pollingVersion]);

  /* Load option chain + build table data */
  useEffect(() => {
    if (!selectedInstrument || !selectedExpiry) return;
    let cancelled = false;
    (async () => {
      const res = await getOptionChainInstrumentsData(selectedInstrument, selectedExpiry);
      if (cancelled || !res) return;
      const payload = res?.data ?? res ?? {};
      const optData = payload.opt?.[selectedExpiry] ?? [];
      setRawOptionChain(optData);
    })();
    return () => { cancelled = true; };
  }, [selectedInstrument, selectedExpiry]);

  /* Process option chain data */
  useEffect(() => {
    if (!rawOptionChain.length || !spotPrice) return;

    const tokenMap = buildStrikeTokenMap(rawOptionChain);
    setStrikeTokenMap(tokenMap);

    const { index: atmIndex, atmStrike: atm } = findATMStrike(rawOptionChain, spotPrice);
    setAtmStrike(atm);

    const strikes = getStrikeList(rawOptionChain, atmIndex, strikeCount);
    setAllStrikes(strikes);

    // Apply selection preset
    const sel = applyStrikeSelection(strikes, atm, selectionPreset);
    setSelectedStrikes(sel);

    // Fetch initial tick data for table
    const allTokens = [];
    strikes.forEach((strike) => {
      if (tokenMap[strike]?.tokenCE) allTokens.push(tokenMap[strike].tokenCE);
      if (tokenMap[strike]?.tokenPE) allTokens.push(tokenMap[strike].tokenPE);
    });

    if (allTokens.length) {
      getTicksData(allTokens.join(',')).then((tickRes) => {
        const ticks = tickRes?.data ?? tickRes ?? {};
        const tableData = {};
        const refData = {};

        strikes.forEach((strike, idx) => {
          const ce = tokenMap[strike]?.tokenCE;
          const pe = tokenMap[strike]?.tokenPE;
          const ceTick = ce ? ticks[ce] : null;
          const peTick = pe ? ticks[pe] : null;

          const ceOi = ceTick?.oi ?? 0;
          const peOi = peTick?.oi ?? 0;
          const cePrevOi = ceTick?.prev?.oi ?? 0;
          const pePrevOi = peTick?.prev?.oi ?? 0;

          tableData[strike] = {
            lpCE: ceTick?.lp ?? 0,
            lpPE: peTick?.lp ?? 0,
            oiCE: ceOi,
            oiPE: peOi,
            oiChgCE: ceOi - cePrevOi,
            oiChgPE: peOi - pePrevOi,
            volCE: ceTick?.v ?? 0,
            volPE: peTick?.v ?? 0,
            ivCE: ceTick?.iv ?? 0,
            ivPE: peTick?.iv ?? 0,
            colorCE: getSeriesColor(idx * 2),
            colorPE: getSeriesColor(idx * 2 + 1),
          };

          refData[strike] = {
            CE: { oi: cePrevOi, lp: ceTick?.prev?.p ?? 0, iv: ceTick?.prev?.iv ?? 0 },
            PE: { oi: pePrevOi, lp: peTick?.prev?.p ?? 0, iv: peTick?.prev?.iv ?? 0 },
          };
        });

        setStrikeTableData(tableData);
        setReferenceData(refData);
        tableDataRef.current = tableData;
      });
    }
  }, [rawOptionChain, spotPrice, strikeCount, pollingVersion]);


  const criticalPoints = useMemo(() => {
    const points = {
      maxOiCE: 0,
      minOiCE: Infinity,
      maxOiPE: 0,
      minOiPE: Infinity,
      maxVolCE: 0,
      minVolCE: Infinity,
      maxVolPE: 0,
      minVolPE: Infinity,
    };
    const rows = Object.values(strikeTableData);
    if (rows.length === 0) return points;

    rows.forEach((row) => {
      // CE OI
      if (row.oiCE > points.maxOiCE) points.maxOiCE = row.oiCE;
      if (row.oiCE > 0 && row.oiCE < points.minOiCE) points.minOiCE = row.oiCE;

      // PE OI
      if (row.oiPE > points.maxOiPE) points.maxOiPE = row.oiPE;
      if (row.oiPE > 0 && row.oiPE < points.minOiPE) points.minOiPE = row.oiPE;

      // CE Vol
      if (row.volCE > points.maxVolCE) points.maxVolCE = row.volCE;
      if (row.volCE > 0 && row.volCE < points.minVolCE) points.minVolCE = row.volCE;

      // PE Vol
      if (row.volPE > points.maxVolPE) points.maxVolPE = row.volPE;
      if (row.volPE > 0 && row.volPE < points.minVolPE) points.minVolPE = row.volPE;
    });

    // Cleanup Infinity if no positive values found
    if (points.minOiCE === Infinity) points.minOiCE = 0;
    if (points.minOiPE === Infinity) points.minOiPE = 0;
    if (points.minVolCE === Infinity) points.minVolCE = 0;
    if (points.minVolPE === Infinity) points.minVolPE = 0;

    criticalPointsRef.current = points;
    return points;
  }, [strikeTableData]);


  const fetchChartBars = useCallback(async () => {
    if (!selectedStrikes.length || !Object.keys(strikeTokenMap).length || !Object.keys(strikeTableData).length) {
      setChartData(null);
      return;
    }

    setChartLoading(true);

    const tokenList = [];
    selectedStrikes.forEach(({ strike, enableCE, enablePE }) => {
      const map = strikeTokenMap[strike];
      if (!map) return;
      if (enableCE && map.tokenCE) tokenList.push(String(map.tokenCE));
      if (enablePE && map.tokenPE) tokenList.push(String(map.tokenPE));
    });

    if (!tokenList.length) { setChartData(null); setChartLoading(false); return; }

    const { fromStr, toStr } = calculateChartDates(period, holidays, specialSessions);
    const resType = resolution;

    const raw = await fetchBarsIntraday({
      token: tokenList.join(','),
      type: resType,
      from: fromStr,
      to: toStr,
    });

    if (!raw) { setChartData(null); setChartLoading(false); return; }

    const data = raw?.data ?? {};
    if (!Object.keys(data).length) { setChartData(null); setChartLoading(false); return; }

    // Build series data per token
    const seriesMap = {};
    const pcrMap = {};
    const tickRef = referenceData;

    selectedStrikes.forEach(({ strike, enableCE, enablePE }) => {
      const map = strikeTokenMap[strike];
      const tableEntry = strikeTableData[strike];
      if (!map || !tableEntry) return;

      const ceToken = String(map.tokenCE || '');
      const peToken = String(map.tokenPE || '');
      const ceData = ceToken ? data[ceToken] : null;
      const peData = peToken ? data[peToken] : null;

      if (mode === 'pcr' && ceData && peData) {
        const points = [];
        ceData.t?.forEach((t, i) => {
          const ceOi = ceData.oi?.[i];
          const peOi = peData.oi?.[i];
          if (ceOi && ceOi > 0) {
            points.push({ time: timeToLocal(t), value: (peOi || 0) / ceOi });
          }
        });
        pcrMap[strike] = { points, color: tableEntry.colorCE, label: `Strike ${strike}` };
      } else if (mode === 'iv') {
        if (enableCE && ceData) {
          const points = ceData.t?.map((t, i) => ({
            time: timeToLocal(t),
            value: ceData.ivc?.[i] ?? ceData.c?.[i] ?? 0,
          })) || [];
          seriesMap[`${strike}_CE`] = {
            points, color: tableEntry.colorCE, label: `${strike} CE`,
          };
        }
      } else {
        const field = mode === 'oi' ? 'oi' : 'c';
        const atpField = 'atp';

        if (enableCE && ceData) {
          const points = ceData.t?.map((t, i) => ({
            time: timeToLocal(t), value: ceData[field]?.[i] ?? 0,
          })) || [];
          const atpPoints = mode === 'lp' ? (ceData.t?.map((t, i) => ({
            time: timeToLocal(t), value: ceData[atpField]?.[i] ?? 0,
          })) || []) : [];

          const oicPoints = mode === 'oi' && tickRef[strike]?.CE?.oi
            ? (ceData.t?.map((t, i) => ({
              time: timeToLocal(t), value: (ceData.oi?.[i] ?? 0) - tickRef[strike].CE.oi,
            })) || [])
            : [];

          seriesMap[ceToken] = {
            points, atpPoints, oicPoints,
            color: tableEntry.colorCE,
            label: `${strike} CE`,
            strike, type: 'CE',
          };
        }
        if (enablePE && peData) {
          const points = peData.t?.map((t, i) => ({
            time: timeToLocal(t), value: peData[field]?.[i] ?? 0,
          })) || [];
          const atpPoints = mode === 'lp' ? (peData.t?.map((t, i) => ({
            time: timeToLocal(t), value: peData[atpField]?.[i] ?? 0,
          })) || []) : [];

          const oicPoints = mode === 'oi' && tickRef[strike]?.PE?.oi
            ? (peData.t?.map((t, i) => ({
              time: timeToLocal(t), value: (peData.oi?.[i] ?? 0) - tickRef[strike].PE.oi,
            })) || [])
            : [];

          seriesMap[peToken] = {
            points, atpPoints, oicPoints,
            color: tableEntry.colorPE,
            label: `${strike} PE`,
            strike, type: 'PE',
          };
        }
      }
    });

    // Build combined series for LP mode
    let combinedPoints = null;
    let combinedAtpPoints = null;
    if (mode === 'lp' && showCombined) {
      const sumMap = {};
      const sumAtpMap = {};
      Object.values(seriesMap).forEach(({ points, atpPoints }) => {
        points.forEach(({ time, value }) => {
          sumMap[time] = (sumMap[time] || 0) + value;
        });
        atpPoints?.forEach(({ time, value }) => {
          sumAtpMap[time] = (sumAtpMap[time] || 0) + value;
        });
      });
      combinedPoints = Object.entries(sumMap)
        .map(([time, value]) => ({ time: Number(time), value }))
        .sort((a, b) => a.time - b.time);
      if (showAtp) {
        combinedAtpPoints = Object.entries(sumAtpMap)
          .map(([time, value]) => ({ time: Number(time), value }))
          .sort((a, b) => a.time - b.time);
      }
    }

    setChartData({
      seriesMap: mode === 'pcr' ? pcrMap : seriesMap,
      combinedPoints,
      combinedAtpPoints,
    });
    setChartLoading(false);
  }, [selectedStrikes, strikeTokenMap, mode, resolution, period, isDark, showCombined, showAtp, referenceData, holidays, specialSessions]);

  useEffect(() => {
    fetchChartBars();
  }, [fetchChartBars, pollingVersion]);

  /* Polling fallback for delayed feed */
  useEffect(() => {
    if (isLiveFeedActive || !isMarketOpen) {
      clearTimeout(pollingTimeoutRef.current);
      clearInterval(pollingIntervalRef.current);
      return;
    }

    const triggerRefresh = () => {
      setPollingVersion((v) => v + 1);
    };

    const secondsToNextMin = (63 - new Date().getSeconds()) * 1000;
    pollingTimeoutRef.current = setTimeout(() => {
      triggerRefresh();
      pollingIntervalRef.current = setInterval(triggerRefresh, 60000);
    }, secondsToNextMin);

    return () => {
      clearTimeout(pollingTimeoutRef.current);
      clearInterval(pollingIntervalRef.current);
    };
  }, [isLiveFeedActive, isMarketOpen]);

  // Throttled React state flusher for Multi Strike selector table (every 5 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      if (hasPendingUpdatesRef.current) {
        setStrikeTableData((prev) => {
          const next = { ...prev, ...pendingUpdatesRef.current };
          tableDataRef.current = next;
          return next;
        });
        pendingUpdatesRef.current = {};
        hasPendingUpdatesRef.current = false;
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleInstrumentChange = useCallback((value) => {
    updateLocal(storageKey, { instrument: value, expiryDate: '' });
    setRawOptionChain([]);
    setSpotPrice(null);
    setSelectedExpiry('');
    setSelectedStrikes([]);
    setChartData(null);
    setSelectedInstrument(value);
    firstLoadRef.current = true;
  }, [storageKey]);

  const handleExpiryChange = useCallback((value) => {
    updateLocal(storageKey, { expiryDate: value });
    setSelectedExpiry(value);
  }, [storageKey]);

  const handleStrikeCountChange = useCallback((value) => {
    updateLocal(storageKey, { strikeCount: value });
    setStrikeCount(value);
  }, [storageKey]);

  const handleSelectionPresetChange = useCallback((value) => {
    updateLocal(storageKey, { preset: value });
    setSelectionPreset(value);
    const sel = applyStrikeSelection(allStrikes, atmStrike, value);
    setSelectedStrikes(sel);
  }, [storageKey, allStrikes, atmStrike]);

  const handleResolutionChange = useCallback((value) => {
    updateLocal(storageKey, { resolution: value });
    setResolution(value);
  }, [storageKey]);

  const handlePeriodChange = useCallback((value) => {
    updateLocal(storageKey, { period: value });
    setPeriod(value);
  }, [storageKey]);

  const handleToggleStrike = useCallback((strike, type, checked) => {
    setSelectedStrikes((prev) => {
      const existing = prev.find((s) => s.strike === strike);
      let updated;
      if (existing) {
        updated = prev.map((s) => {
          if (s.strike !== strike) return s;
          if (type === 'ce') return { ...s, enableCE: checked };
          if (type === 'pe') return { ...s, enablePE: checked };
          return { ...s, enableCE: checked, enablePE: checked };
        });
      } else {
        const enableCE = type === 'ce' || type === 'strike' ? checked : false;
        const enablePE = type === 'pe' || type === 'strike' ? checked : false;
        updated = [...prev, { strike, enableCE, enablePE }];
      }
      return updated.filter((s) => s.enableCE || s.enablePE);
    });
  }, []);

  const handleCombinedChange = useCallback((checked) => {
    updateLocal(storageKey, { combined: checked });
    setShowCombined(checked);
  }, [storageKey]);

  const handleAtpChange = useCallback((checked) => {
    updateLocal(storageKey, { atp: checked });
    setShowAtp(checked);
  }, [storageKey]);

  const handleIndividualChange = useCallback((checked) => {
    updateLocal(storageKey, { individual: checked });
    setShowIndividual(checked);
  }, [storageKey]);

  return {
    mode,
    instruments,
    selectedInstrument,
    expiryOptions,
    selectedExpiry,
    strikeCount,
    selectionPreset,
    resolution,
    period,
    showCombined,
    showAtp,
    showIndividual,
    oiView,
    showStrikePanel,
    isLoading,
    isDark,
    isLiveFeedActive,
    isMarketOpen,
    spotPrice,
    atmStrike,
    strikeTokenMap,
    allStrikes,
    selectedStrikes,
    strikeTableData,
    criticalPoints,
    chartData,
    chartLoading,
    onChangeInstrument: handleInstrumentChange,
    onChangeExpiry: handleExpiryChange,
    onChangeStrikeCount: handleStrikeCountChange,
    onChangeSelectionPreset: handleSelectionPresetChange,
    onChangeResolution: handleResolutionChange,
    onChangePeriod: handlePeriodChange,
    onToggleStrike: handleToggleStrike,
    onChangeCombined: handleCombinedChange,
    onChangeAtp: handleAtpChange,
    onChangeIndividual: handleIndividualChange,
    onChangeOiView: setOiView,
    onToggleStrikePanel: () => setShowStrikePanel((v) => !v),
  };
}
